/**
 * i18n (国际化) 核心功能
 * 支持多语言切换和文本管理
 */

import { useState, useEffect, createContext, useContext } from 'react';
import zh from './zh';
import en from './en';

// 支持的语言列表
export const SUPPORTED_LANGUAGES = {
  en: 'English',
  zh: '中文'
};

// 所有翻译文本
const translations = {
  en,
  zh
};

// 默认语言设置
const DEFAULT_LANGUAGE = 'en';

// 创建国际化上下文
const I18nContext = createContext(null);

/**
 * 国际化提供者组件
 * 为整个应用提供国际化支持
 */
export function I18nProvider({ children }) {
  // 从localStorage获取语言设置或使用默认语言
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage && SUPPORTED_LANGUAGES[savedLanguage] 
      ? savedLanguage 
      : DEFAULT_LANGUAGE;
  });

  // 当语言变化时保存到localStorage
  useEffect(() => {
    localStorage.setItem('language', currentLanguage);
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  /**
   * 切换语言
   * @param {string} language - 目标语言代码
   */
  const changeLanguage = (language) => {
    if (SUPPORTED_LANGUAGES[language]) {
      setCurrentLanguage(language);
    }
  };

  /**
   * 翻译文本
   * @param {string} key - 翻译键
   * @param {Object} params - 替换参数
   * @returns {string} 翻译后的文本
   */
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    // 深度查找翻译值
    for (const k of keys) {
      if (value === undefined) break;
      value = value[k];
    }
    
    // 如果没有找到翻译，使用英文或键名
    if (value === undefined) {
      value = getValueFromEnglishOrKey(key) || key;
    }
    
    // 替换参数
    if (typeof value === 'string' && params) {
      return Object.entries(params).reduce((acc, [paramKey, paramValue]) => {
        return acc.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
      }, value);
    }
    
    return value;
  };
  
  /**
   * 从英文翻译中获取值
   * @param {string} key - 翻译键
   * @returns {string|undefined} 英文翻译或undefined
   */
  const getValueFromEnglishOrKey = (key) => {
    if (currentLanguage === 'en') return undefined;
    
    const keys = key.split('.');
    let value = translations['en'];
    
    for (const k of keys) {
      if (value === undefined) break;
      value = value[k];
    }
    
    return value;
  };

  const contextValue = {
    currentLanguage,
    changeLanguage,
    t,
    SUPPORTED_LANGUAGES
  };

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * 使用国际化钩子
 * @returns {Object} 国际化函数和状态
 */
export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n必须在I18nProvider内部使用');
  }
  return context;
}

/**
 * 静态翻译函数(无上下文，用于服务端或非React环境)
 * @param {string} key - 翻译键
 * @param {string} language - 语言代码
 * @param {Object} params - 替换参数
 * @returns {string} 翻译后的文本
 */
export function translate(key, language = DEFAULT_LANGUAGE, params = {}) {
  const lang = SUPPORTED_LANGUAGES[language] ? language : DEFAULT_LANGUAGE;
  const keys = key.split('.');
  let value = translations[lang];
  
  for (const k of keys) {
    if (value === undefined) break;
    value = value[k];
  }
  
  if (value === undefined) {
    value = key;
  }
  
  if (typeof value === 'string' && params) {
    return Object.entries(params).reduce((acc, [paramKey, paramValue]) => {
      return acc.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
    }, value);
  }
  
  return value;
}

export default {
  I18nProvider,
  useI18n,
  translate,
  SUPPORTED_LANGUAGES
}; 